import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import axios from "@utils/axios";
import { PricingResponse } from "@/types/pricing";

export interface PricingParams {
  evseID: string;
}

const fetchPricing = async (params: PricingParams, signal?: AbortSignal): Promise<PricingResponse> => {
  const response = await axios.get(`/api/evse/${params.evseID}/get-price-details/`, { signal });
  return response.data as PricingResponse;
};

type QueryKeyType = readonly ["pricing", "list", PricingParams | "disabled"];

export const pricingKeys = {
  all: ["pricing"] as const,
  lists: () => [...pricingKeys.all, "list"] as const,
  list: (params: PricingParams | "disabled") => [...pricingKeys.lists(), params] as const
};

export const usePricing = (
  params: PricingParams | null,
  options?: Omit<UseQueryOptions<PricingResponse, Error, PricingResponse, QueryKeyType>, "queryKey" | "queryFn">
) => {
  const isEnabled = params !== null && !!params.evseID;

  return useQuery({
    queryKey: params ? pricingKeys.list(params) : pricingKeys.list("disabled"),
    queryFn: ({ signal }) => {
      if (!params) {
        throw new Error("Pricing params are required");
      }
      return fetchPricing(params, signal);
    },
    staleTime: 60 * 1000, // 1 minute,
    cacheTime: 2 * 60 * 1000, // 2 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    enabled: isEnabled,
    ...options
  });
};
